/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import ResourceForm from "components/forms/ResourceForm";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import ButtonDialog from "components/generals/ButtonDialog";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { CoreAPI_URLs } from "constants/http.const";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import useHttp from "hooks/http.hook";
import { TableProps } from "interfaces";
import { HeaderEnum, ResourceInterface } from "@malmur-technologies/medicalsystem-shared";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectIsPatientOrDoctor } from "store/slices/auth.slice";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { getNewResourceDataContent } from "utils/model.utils";

/**
 * Resource table component for displaying and managing medical resource records.
 * 
 * Features:
 * - Displays resource information in sortable data table
 * - Columns: resource name, type, availability, action buttons
 * - Edit resource functionality via modal with ResourceForm
 * - Delete resource with confirmation dialog and list update
 * - Permission-based access control (read-only for patients/doctors)
 * - Conditional column display based on user role
 * - Automatic modal action type management
 * 
 * Resource Types:
 * - Medical equipment (MRI, X-ray, ultrasound)
 * - Examination rooms
 * - Operating rooms
 * - Medical devices
 * - Lab equipment
 * - Treatment facilities
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - Permission-based rendering via PermissionWrapper
 * - Read-only mode for patients and doctors
 * - Full CRUD for administrators
 * 
 * @param {TableProps<ResourceInterface>} props - Table properties
 * @param {ResourceInterface[]} props.modelList - Array of resource records
 * @param {Function} props.setModelList - Callback to update resource list
 * 
 * @returns {JSX.Element} Resource data table with role-based actions
 */
const ResourceTable: React.FC<TableProps<ResourceInterface>> = ({
  modelList,
  setModelList,
  ...props
}) => {
  const [selectedResource, setSelectedResource] = useState<ResourceInterface>(
    getNewResourceDataContent()
  );
  const [activeEditResourceModal, setActiveEditResourceModal] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<FormActionType>();
  const { t } = useTranslation();
  const currentUser = useSelector((state: any) => state.auth.user);
  const [_isLoading, _error, sendRequest] = useHttp();

  const isPatientOrDoctor = useSelector(selectIsPatientOrDoctor);

  const openModalHandler = (resource: ResourceInterface) => {
    setSelectedResource(resource);
    setActiveEditResourceModal(true);
  };

  const deleteHandler = (resourceTemplate: ResourceInterface) => {
    setSelectedResource(resourceTemplate);
    sendRequest<boolean, ResourceInterface>(
      CoreAPI_URLs.RESOURCE_GENERAL_OPERATIONS,
      onResourceTemplateDeleteHandler,
      "DELETE",
      JSON.stringify(resourceTemplate),
      [{ header: HeaderEnum.RESOURCE_ID, value: resourceTemplate.id }],
      { payload: resourceTemplate, successMessage: t("resourceTable.resourceDeleted") }
    );
  };

  const onResourceTemplateDeleteHandler = (
    result: boolean,
    resourceTemplate: ResourceInterface
  ) => {
    const indexList = modelList.indexOf(resourceTemplate);
    modelList.splice(indexList, 1);
    setModelList([...modelList]);
  };

  const getUITable = (list: ResourceInterface[]) => {
    const baseColumns = [
      {
        Header: t("resourceTable.name"),
        accessor: "name",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("resourceTable.facility"),
        accessor: "location.name",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("resourceTable.phone"),
        accessor: "phone",
        sortIndicatorType: "numeric",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("resourceTable.defaultApptProv"),
        accessor: "defaultAppointmentProvider",
        Cell: ({ value }: any) => (
          <DefaultCell
            value={
              value?.person?.name && value?.person?.lastname
                ? `${value.person.name} ${value.person.lastname}`
                : ""
            }
          />
        ),
      },
    ];
    const actionsColumn = {
      Header: t("resourceTable.actions"),
      accessor: "id",
      disableSortBy: true,
      Cell: ({ value }: any) => (
        <Box display="flex" gap={1}>
          <Button
            name="viewResourceButton"
            variant="contained"
            color="info"
            onClick={() => {
              const resource = list.find((p) => p.id === value);
              if (resource) {
                setModalAction(FormActionType.MODIFY);
                openModalHandler(resource);
              }
            }}
          >
            {isPatientOrDoctor ? t("resourceTable.viewAction") : t("resourceTable.editAction")}
          </Button>
          <PermissionWrapper
            requiredPermissions={[PermissionsUser[EntityPermissions.RESOURCE].DELETE]}
          >
            <ButtonDialog
              name="deleteResourceButton"
              onAcceptHandle={() => {
                const resource = list.find((p) => p.id === value);
                if (resource) {
                  deleteHandler(resource);
                }
              }}
              buttonText={t("resourceTable.deleteAction")}
              title={t("resourceTable.deleteConfirmation")}
              description={t("resourceTable.description")}
              labelledbyID={"ResourceDelete"}
              describedbyID={"ResourceDescriptionId"}
            />
          </PermissionWrapper>
        </Box>
      ),
    };

    baseColumns.push(actionsColumn);

    return {
      columns: baseColumns,
      rows: list,
    };
  };

  const dataTableData = useMemo(() => getUITable(modelList), [modelList, currentUser]);

  return (
    <>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end">
        <PermissionWrapper
          requiredPermissions={[PermissionsUser[EntityPermissions.RESOURCE].CREATE]}
        >
          <Button
            name="newResourceButton"
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              setModalAction(FormActionType.NEW);
              setSelectedResource(getNewResourceDataContent());
              setActiveEditResourceModal(true);
            }}
          >
            {t("resourceForm.newResource")}
          </Button>
        </PermissionWrapper>
      </Box>
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}
        onSortChange={props.onSortChange}
        currentSortBy={props.currentSortBy}
        currentSortOrder={props.currentSortOrder}
        />
      <Modal
        open={activeEditResourceModal}
        onClose={() => {
          setActiveEditResourceModal(false);
        }}
      >
        <ResourceForm
          modelList={modelList}
          setModelList={setModelList}
          action={modalAction}
          model={selectedResource}
          onCloseAction={() => {
            setActiveEditResourceModal(false);
          }}
          isModal={true}
        />
      </Modal>
    </>
  );
};

export default ResourceTable;
